import { useState, useCallback, useRef } from 'react';
import profileService from '../services/profileService';
import { useMessageAlert } from '../components/common/MessageAlert';
import i18n from '../i18n';

/**
 * Hook to manage current user's profile
 * - getProfile(force) - fetches /users/me (cached)
 * - updateProfile(payload) - updates profile and updates local state/cache
 */
const useProfile = () => {
  // State
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // dedupe / in-flight tracking
  const inFlightRef = useRef(false);
  const lastFetchAtRef = useRef(0);

  const { showUpdate } = useMessageAlert();

  const fetchProfile = useCallback(async ({ force = false } = {}) => {
    if (inFlightRef.current && !force) {
      return { success: true, fromCache: true };
    }

    // If profile already loaded and not forcing, return it
    if (profile && !force) {
      return { success: true, data: profile, fromCache: true };
    }

    inFlightRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const result = await profileService.getProfile(force);
      if (result.success) {
        setProfile(result.data);
      } else {
        setError(result.error);
      }
      lastFetchAtRef.current = Date.now();
      return result;
    } catch (err) {
      const msg = err.message;
      setError(msg);
      return { success: false, error: msg };
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  }, [profile]);

  const updateProfile = useCallback(async (payload) => {
    setLoading(true);
    setError(null);

    try {
      const result = await profileService.updateProfile(payload);
      if (result.success) {
        setProfile(result.data);
        showUpdate(i18n.t('profile.notifications.updated'));
      } else {
        setError(result.error);
      }
      return result;
    } catch (err) {
      const msg = err.message;
      setError(msg);
      return { success: false, error: msg };
    } finally {
      setLoading(false);
    }
  }, [showUpdate]);

  const resetPassword = useCallback(async (payload) => {
    setLoading(true);
    setError(null);

    try {
      const result = await profileService.resetPassword(payload);
      if (!result.success) {
        setError(result.error);
      }
      return result;
    } catch (err) {
      const msg = err.message;
      setError(msg);
      return { success: false, error: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);
  const clearProfileCache = useCallback(() => profileService.clearCache(), []);

  return {
    profile,
    loading,
    error,
    fetchProfile,
    updateProfile,
    resetPassword,
    clearError,
    clearProfileCache,
  };
};

export default useProfile;
